import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Textarea } from '@amore/ui/components/textarea'
import { type Mail, NotebookPen, Plus, Shapes, Trash2, UserRound } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { formatDate, formatMoney, newId, plural } from '@/lib/format'
import { supplierStatusLabels } from '@/lib/labels'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type {
  SupplierStatus as Status,
  Supplier,
  SupplierContact,
  SupplierProfile,
  WeddingData,
} from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { ItemAddRow, ItemCard, ItemCardFields } from '@/patterns/item-card'
import { LinkedRecord, linkedModuleConfig } from '@/patterns/linked-record'
import { useToast } from '@/shell/toaster'
import { scheduleSignature } from '../schedule/model'
import { categoryLabel, categoryOrder, isAgreed, sectionOf } from './model'
import { ProfilePane } from './profile-pane'
import { RemoveConsequences, removeSupplier } from './remove'
import { SupplierStatus } from './supplier-status'

export const sheetTabs = ['work', 'contacts', 'profile'] as const
export type SheetTab = (typeof sheetTabs)[number]

const tabLabels: Record<SheetTab, string> = {
  work: 'Spolupráce',
  contacts: 'Kontakty',
  profile: 'Veřejný profil',
}

/** The statuses in the order the cooperation goes. */
const _statuses: Status[] = ['inquiry', 'agreed', 'booked', 'cancelled']

type Draft = Pick<Supplier, 'name' | 'category' | 'notes' | 'contacts'> & {
  profile: SupplierProfile
}

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

/**
 * A supplier's detail, in three tabs: Spolupráce (category, the main contact,
 * where the supplier shows up in the plan, notes), Kontakty (the people) and
 * Profil (web, e-mail, phone, social; read-only for catalog suppliers). The
 * status is the pill under the name and applies at once; the rest saves with
 * "Uložit".
 */
export function SupplierSheet({
  supplier,
  initialTab = 'work',
  onClose,
}: {
  supplier: Supplier
  initialTab?: SheetTab
  onClose: () => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const catalog = data.catalog.find((item) => item.id === supplier.catalogServiceId)
  const [tab, setTab] = useState<SheetTab>(initialTab)
  const [draft, setDraft] = useState<Draft>(() => draftOf(supplier))
  const [nameError, setNameError] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(supplier))
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))

  const setStatus = (status: Status) => {
    if (status === supplier.status) return
    update((current) => ({
      ...current,
      suppliers: replaceById(current.suppliers, supplier.id, {
        status,
        statusChangedAt: '2026-10-19',
      }),
    }))
    showToast({
      title: 'Stav změněn',
      description: `${supplier.name}: ${supplierStatusLabels[status]}`,
      tone: 'success',
    })
  }
  const save = () => {
    if (!draft.name.trim()) {
      setNameError(true)
      showToast({ title: 'Doplňte název dodavatele', tone: 'danger' })
      return
    }
    update((current) => ({
      ...current,
      suppliers: replaceById(current.suppliers, supplier.id, {
        name: draft.name.trim(),
        category: draft.category,
        notes: draft.notes,
        contacts: draft.contacts.filter(
          (contact) => contact.name.trim() || contact.phone || contact.email,
        ),
        ...(catalog ? {} : { profile: draft.profile }),
      }),
    }))
    showToast({ title: 'Dodavatel uložen', description: draft.name.trim(), tone: 'success' })
    onClose()
  }

  const profilePane = (
    <ProfilePane
      name={draft.name}
      category={draft.category}
      catalog={catalog ? { id: catalog.id, region: catalog.region } : undefined}
      profile={
        catalog
          ? { description: catalog.description, website: catalog.website, ...catalog.profile }
          : draft.profile
      }
      editable={canEdit && !catalog}
      onChange={(profile) => set({ profile })}
      onNameChange={(name) => {
        set({ name })
        setNameError(false)
      }}
    />
  )

  return (
    <>
      <DetailSheet
        open
        aside={profilePane}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        title={supplier.name}
        dirty={dirty}
        titleContent={
          <div className="space-y-4">
            {/* On lg the public profile on the left carries the name; the edit column doesn't repeat it. */}
            <div className="space-y-1 lg:hidden">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-action">
                {categoryLabel(draft.category)}
              </p>
              <textarea
                value={draft.name}
                rows={1}
                readOnly={!canEdit || Boolean(catalog)}
                aria-label="Název dodavatele"
                aria-invalid={nameError || undefined}
                onChange={(event) => {
                  set({ name: event.target.value })
                  setNameError(false)
                }}
                className={cn(
                  'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                  canEdit && !catalog && 'hover:bg-muted/60 focus:bg-muted/60',
                  supplier.status === 'cancelled' && 'text-muted-foreground',
                )}
              />
              {nameError ? (
                <p className="text-sm text-error-foreground">Doplňte název dodavatele.</p>
              ) : null}
            </div>
            <StatusStepper status={supplier.status} canEdit={canEdit} onChange={setStatus} />
            <div role="tablist" aria-label="Části detailu" className="-mb-5 flex gap-7 pt-1">
              {sheetTabs.map((value) => {
                const current = value === tab
                const count = value === 'contacts' ? draft.contacts.length : 0
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={current}
                    onClick={() => setTab(value)}
                    className={cn(
                      value === 'profile' && 'lg:hidden',
                      '-mb-px inline-flex items-center gap-1.5 border-b-2 pb-3 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                      current
                        ? 'border-action text-action'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tabLabels[value]}
                    {count > 0 ? (
                      <span className="text-xs font-medium text-muted-foreground tabular-nums">
                        {count}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        }
        primaryAction={canEdit ? { label: 'Uložit', onClick: save } : undefined}
        secondaryAction={
          canEdit
            ? supplier.status === 'cancelled'
              ? { label: 'Obnovit spolupráci', onClick: () => setStatus('inquiry') }
              : { label: 'Ukončit spolupráci', onClick: () => setStatus('cancelled') }
            : undefined
        }
        destructiveAction={
          canEdit
            ? {
                label: 'Odebrat dodavatele',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmRemove(true),
              }
            : undefined
        }
      >
        {tab === 'work' ? (
          <WorkTab
            supplier={supplier}
            draft={draft}
            data={data}
            canEdit={canEdit}
            set={set}
            onContacts={() => setTab('contacts')}
          />
        ) : tab === 'contacts' ? (
          <ContactsTab
            contacts={draft.contacts}
            canEdit={canEdit}
            onChange={(contacts) => set({ contacts })}
          />
        ) : (
          // Below lg the public profile has no room on the left, so it's a tab here.
          <div className="-mx-6 -my-5 lg:hidden">{profilePane}</div>
        )}
      </DetailSheet>
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        heading={`Odebrat dodavatele ${supplier.name}?`}
        description={<RemoveConsequences supplier={supplier} data={data} />}
        keepLabel="Ponechat dodavatele"
        confirmLabel="Odebrat dodavatele"
        onConfirm={() => {
          const previous = data
          update((current) => removeSupplier(current, supplier.id))
          showToast({
            title: 'Dodavatel odebrán',
            description: supplier.name,
            action: {
              label: 'Vrátit zpět',
              altText: 'Vrátit odebraného dodavatele',
              onClick: () => update(() => previous),
            },
          })
          onClose()
        }}
      />
    </>
  )
}

function draftOf(supplier: Supplier): Draft {
  return {
    name: supplier.name,
    category: supplier.category,
    notes: supplier.notes,
    contacts: supplier.contacts,
    profile: supplier.profile ?? {},
  }
}

/** The cooperation's steps; Zrušeno sits outside them. */
const steps: Exclude<Status, 'cancelled'>[] = ['inquiry', 'agreed', 'booked']

const stepTones: Record<Exclude<Status, 'cancelled'>, string> = {
  inquiry: 'bg-muted-foreground/45',
  agreed: 'bg-action',
  booked: 'bg-success-foreground',
}

/**
 * The status as the head of the edit column: the three steps of the
 * cooperation as one bar, filled up to the current one; a click on a step
 * moves there. Ending it ("Ukončit spolupráci" in the footer) replaces the
 * bar with Zrušeno.
 */
function StatusStepper({
  status,
  canEdit,
  onChange,
}: {
  status: Status
  canEdit: boolean
  onChange: (status: Status) => void
}) {
  const cancelled = status === 'cancelled'
  const current = cancelled ? -1 : steps.indexOf(status)
  const fill = cancelled ? '' : stepTones[status]
  return (
    <div className="space-y-2.5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-action">
        Vaše spolupráce
      </p>
      {cancelled ? (
        <div className="flex items-center gap-3 rounded-2xl bg-muted/60 px-4 py-3 text-sm">
          <SupplierStatus status="cancelled" />
          <span className="text-muted-foreground">Spolupráce skončila.</span>
        </div>
      ) : (
        <fieldset aria-label="Stav spolupráce" className="grid grid-cols-3 gap-1.5">
          {steps.map((step, index) => {
            const reached = index <= current
            return (
              <button
                key={step}
                type="button"
                disabled={!canEdit}
                aria-pressed={index === current}
                onClick={() => onChange(step)}
                className="group space-y-2 text-left disabled:cursor-default"
              >
                <span
                  className={cn(
                    'block h-1.5 rounded-full transition-colors',
                    reached ? fill : 'bg-muted group-enabled:group-hover:bg-muted-foreground/25',
                  )}
                />
                <span
                  className={cn(
                    'block text-sm',
                    index === current
                      ? 'font-semibold text-foreground'
                      : 'text-muted-foreground group-enabled:group-hover:text-foreground',
                  )}
                >
                  {supplierStatusLabels[step]}
                </span>
              </button>
            )
          })}
        </fieldset>
      )}
    </div>
  )
}

function WorkTab({
  supplier,
  draft,
  data,
  canEdit,
  set,
  onContacts,
}: {
  supplier: Supplier
  draft: Draft
  data: WeddingData
  canEdit: boolean
  set: (patch: Partial<Draft>) => void
  onContacts: () => void
}) {
  const contact = draft.contacts[0]
  const links = linkedRecords(supplier, data, canEdit)
  return (
    <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
      <fieldset disabled={!canEdit} className="grid grid-cols-2 gap-x-6 gap-y-7">
        <legend className="sr-only">Vlastnosti dodavatele</legend>
        <Tile icon={Shapes} label="Kategorie">
          <Select
            variant="soft"
            className={softControl}
            value={draft.category}
            onValueChange={(category) => set({ category })}
          >
            {categoryOrder.map((value) => (
              <SelectItem key={value} value={value}>
                {categoryLabel(value)}
              </SelectItem>
            ))}
          </Select>
        </Tile>
        <Tile icon={UserRound} label="Hlavní kontakt">
          {contact ? (
            <span className="block space-y-0.5 px-1 text-sm">
              <span className="block font-medium">{contact.name || 'Bez jména'}</span>
              {contact.phone ? (
                <a
                  href={`tel:${contact.phone.replaceAll(' ', '')}`}
                  className="block text-muted-foreground hover:text-foreground hover:underline"
                >
                  {contact.phone}
                </a>
              ) : null}
              {contact.email ? (
                <a
                  href={`mailto:${contact.email}`}
                  className="block truncate text-muted-foreground hover:text-foreground hover:underline"
                >
                  {contact.email}
                </a>
              ) : null}
            </span>
          ) : (
            <Button appearance="outline" tone="neutral" size="sm" onClick={onContacts}>
              <Plus aria-hidden="true" className="size-3.5" /> Přidat kontakt
            </Button>
          )}
        </Tile>
      </fieldset>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Kde figuruje</h3>
        {links.length > 0 ? (
          <div className="space-y-4">{links}</div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Zatím nikde. Až dodavatele domluvíte, objeví se v sekci, harmonogramu i rozpočtu.
          </p>
        )}
      </section>

      <section className="space-y-2.5">
        <h3 className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <NotebookPen aria-hidden="true" className="size-4" /> Poznámky
        </h3>
        <Textarea
          value={draft.notes}
          maxLength={3000}
          readOnly={!canEdit}
          placeholder={canEdit ? 'Co jste domluvili, na co se ptát, jak to šlo…' : 'Bez poznámek'}
          onChange={(event) => set({ notes: event.target.value })}
          className="min-h-28 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
        />
      </section>
    </div>
  )
}

/** Where the supplier is used: its section page, the schedule and the budget. Read-only. */
function linkedRecords(supplier: Supplier, data: WeddingData, canEdit: boolean): ReactNode[] {
  const records: ReactNode[] = []
  const section = sectionOf(supplier.category)
  if (section && isAgreed(supplier))
    records.push(
      <LinkedRecord
        key="section"
        module={section}
        title={linkedModuleConfig(section).label}
        meta="Stránka v plánovači"
        actionLabel="Otevřít sekci"
      />,
    )
  const events = data.schedule.filter(
    (event) => !event.removed && event.owner?.type === 'supplier' && event.owner.id === supplier.id,
  )
  if (events.length > 0) {
    const share = data.scheduleShares.find((item) => item.supplierId === supplier.id)
    const sent = !share
      ? 'Zatím ho nedostal'
      : share.signature === scheduleSignature(data.schedule)
        ? `Poslaný ${formatDate(share.sentAt)}`
        : `Poslaný ${formatDate(share.sentAt)} · mezitím se změnil`
    records.push(
      <LinkedRecord
        key="schedule"
        module="schedule"
        title={`${events.length} ${plural(events.length, 'položka', 'položky', 'položek')} v harmonogramu`}
        meta={sent}
      />,
    )
  }
  const expense = data.budget.expenses.find((item) => item.supplierId === supplier.id)
  if (expense) {
    const paid = expense.payments
      .filter((payment) => payment.paid)
      .reduce((sum, payment) => sum + payment.amount, 0)
    records.push(
      <LinkedRecord
        key="budget"
        module="budget"
        title={expense.amount > 0 ? formatMoney(expense.amount) : 'Bez ceny'}
        meta={
          expense.amount > 0
            ? `Zaplaceno ${formatMoney(paid)} · ${expense.payments.length} ${plural(expense.payments.length, 'platba', 'platby', 'plateb')}`
            : 'Cenu doplňte v rozpočtu'
        }
        actionLabel="Otevřít v rozpočtu"
        to={`/budget/${expense.id}`}
      />,
    )
  } else if (canEdit && isAgreed(supplier))
    records.push(
      <LinkedRecord
        key="budget"
        module="budget"
        title="Zatím není v rozpočtu"
        meta="Přidejte výdaj a platby"
        actionLabel="Přidat do rozpočtu"
        to={`/budget?add=${supplier.id}`}
      />,
    )
  return records
}

function ContactsTab({
  contacts,
  canEdit,
  onChange,
}: {
  contacts: SupplierContact[]
  canEdit: boolean
  onChange: (contacts: SupplierContact[]) => void
}) {
  const patch = (id: string, value: Partial<SupplierContact>) =>
    onChange(replaceById(contacts, id, value))
  const add = () =>
    onChange([...contacts, { id: newId('contact'), name: '', role: '', email: '', phone: '' }])
  return (
    <div className="space-y-4">
      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? 'Zatím tu nikdo není. Přidejte člověka, se kterým jednáte, ať víte, komu zavolat.'
            : 'Zatím bez kontaktní osoby.'}
        </p>
      ) : null}
      {contacts.map((contact, index) => (
        <ItemCard
          key={contact.id}
          index={index + 1}
          title={contact.name}
          fallbackTitle="Nový kontakt"
          meta={index === 0 ? 'Hlavní kontakt' : contact.role || undefined}
          menu={
            canEdit
              ? [
                  ...(index > 0
                    ? [
                        {
                          label: 'Nastavit jako hlavní',
                          onSelect: () =>
                            onChange([
                              contact,
                              ...contacts.filter((item) => item.id !== contact.id),
                            ]),
                        },
                      ]
                    : []),
                  {
                    label: 'Odebrat kontakt',
                    tone: 'danger' as const,
                    onSelect: () => onChange(contacts.filter((item) => item.id !== contact.id)),
                  },
                ]
              : []
          }
        >
          <fieldset disabled={!canEdit}>
            <ItemCardFields>
              <Field label="Jméno">
                <Input
                  value={contact.name}
                  onChange={(event) => patch(contact.id, { name: event.target.value })}
                />
              </Field>
              <Field label="Role">
                <Input
                  value={contact.role ?? ''}
                  placeholder="Např. koordinátorka"
                  onChange={(event) => patch(contact.id, { role: event.target.value })}
                />
              </Field>
              <Field label="E-mail">
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(event) => patch(contact.id, { email: event.target.value })}
                />
              </Field>
              <Field label="Telefon">
                <Input
                  type="tel"
                  value={contact.phone}
                  onChange={(event) => patch(contact.id, { phone: event.target.value })}
                />
              </Field>
            </ItemCardFields>
          </fieldset>
        </ItemCard>
      ))}
      {canEdit ? (
        <ItemAddRow>
          <Button appearance="outline" tone="neutral" size="sm" onClick={add}>
            <Plus aria-hidden="true" className="size-3.5" /> Přidat kontakt
          </Button>
        </ItemAddRow>
      ) : null}
    </div>
  )
}

/** One property: its label above, its control always live. */
function Tile({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Mail
  label: string
  children: ReactNode
}) {
  return (
    <div className="space-y-2.5">
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden="true" />
        {label}
      </span>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
